<template>
<div class="page">
    <ExaminerNavBar />
    <div class="container container--narrow">
        <div class="page-head rise">
            <h1>Create a Question</h1>
            <p>Define the problem, its function signature, and the test cases that grade it.</p>
        </div>

        <form @submit.prevent="addQuestionForm" @reset.prevent="onReset" autocomplete="off">
            <!-- basics -->
            <div class="glass card mb-3 rise rise-1">
                <h2 class="card__title mb-3">01 · Basics</h2>
                <div class="field">
                    <label for="q-name">Question name</label>
                    <input id="q-name" type="text" class="input" placeholder="e.g. Sum of Two Numbers"
                        :class="{'is-invalid': errors.name===false, 'is-valid': errors.name===true}"
                        v-model="question.name" @input="validateName">
                    <p class="err" v-if="errors.name===false">Name is required</p>
                </div>
                <div class="field">
                    <label for="q-statement">Statement</label>
                    <textarea id="q-statement" class="textarea" placeholder="Describe the problem…"
                        :class="{'is-invalid': errors.statement===false, 'is-valid': errors.statement===true}"
                        v-model="question.statement" @input="validateStatement"></textarea>
                    <p class="err" v-if="errors.statement===false">Statement is required</p>
                </div>
                <div class="field">
                    <label>Difficulty</label>
                    <div class="flex gap-2 flex-wrap">
                        <label class="radio-pill"><input type="radio" value="easy" v-model="question.difficulty" @change="validateDifficulty"><span>Easy</span></label>
                        <label class="radio-pill"><input type="radio" value="medium" v-model="question.difficulty" @change="validateDifficulty"><span>Medium</span></label>
                        <label class="radio-pill"><input type="radio" value="hard" v-model="question.difficulty" @change="validateDifficulty"><span>Hard</span></label>
                    </div>
                    <p class="err" v-if="errors.difficulty===false">Difficulty is required</p>
                </div>
            </div>

            <!-- signature -->
            <div class="glass card mb-3 rise rise-2">
                <h2 class="card__title mb-3">02 · Function Signature</h2>
                <div class="field">
                    <label>Input parameters</label>
                    <div v-for="i in inputFormatArraySize" :key="'in'+i" class="flex gap-2 mb-2 flex-wrap">
                        <select class="select" style="max-width:150px"
                            :class="{'is-invalid': errors.inputFormat.type[i-1]===false, 'is-valid': errors.inputFormat.type[i-1]===true}"
                            v-model="question.inputFormat.type[i-1]" @change="validateInputTypeDescription(i-1)">
                            <option value="" disabled>Type…</option>
                            <option value="int">Integer</option>
                            <option value="String">String</option>
                        </select>
                        <input type="text" class="input" style="flex:1;min-width:200px" placeholder="What is this parameter?"
                            :class="{'is-invalid': errors.inputFormat.description[i-1]===false, 'is-valid': errors.inputFormat.description[i-1]===true}"
                            v-model="question.inputFormat.description[i-1]" @input="validateInputFormatDescription(i-1)">
                    </div>
                    <p class="err" v-if="errors.inputFormat.type.some(v=>v===false)">Every parameter needs a type</p>
                    <p class="err" v-if="errors.inputFormat.description.some(v=>v===false)">Every parameter needs a description</p>
                    <div class="flex gap-2 mt-2">
                        <button type="button" class="btn btn--ghost btn--sm" @click="increaseInputArraySize">＋ Add parameter</button>
                        <button type="button" class="btn btn--ghost btn--sm" v-if="inputFormatArraySize>1" @click="decreaseInputArraySize">− Remove</button>
                    </div>
                </div>
                <div class="field">
                    <label>Return value</label>
                    <div class="flex gap-2 flex-wrap">
                        <select class="select" style="max-width:150px"
                            :class="{'is-invalid': errors.outputFormat.type===false, 'is-valid': errors.outputFormat.type===true}"
                            v-model="question.outputFormat.type" @change="validateOutputTypeDescription">
                            <option value="" disabled>Type…</option>
                            <option value="int">Integer</option>
                            <option value="String">String</option>
                        </select>
                        <input type="text" class="input" style="flex:1;min-width:200px" placeholder="What does the function return?"
                            :class="{'is-invalid': errors.outputFormat.description===false, 'is-valid': errors.outputFormat.description===true}"
                            v-model="question.outputFormat.description" @input="validateOutputFormatDescription">
                    </div>
                    <p class="err" v-if="errors.outputFormat.type===false">Return type is required</p>
                    <p class="err" v-if="errors.outputFormat.description===false">Return description is required</p>
                </div>
            </div>

            <!-- constraints -->
            <div class="glass card mb-3 rise rise-3">
                <h2 class="card__title mb-3">03 · Constraints</h2>
                <div v-for="i in constraintsArraySize" :key="'c'+i" class="field" style="margin-bottom:10px">
                    <input type="text" class="input mono" placeholder="e.g. -10^9 <= a, b <= 10^9"
                        :class="{'is-invalid': errors.constraints[i-1]===false, 'is-valid': errors.constraints[i-1]===true}"
                        v-model="question.constraints[i-1]" @input="validateConstraints(i-1)">
                    <p class="err" v-if="errors.constraints[i-1]===false">Constraint is required</p>
                </div>
                <div class="flex gap-2 mt-2">
                    <button type="button" class="btn btn--ghost btn--sm" @click="increaseConstraintsArraySize">＋ Add constraint</button>
                    <button type="button" class="btn btn--ghost btn--sm" v-if="constraintsArraySize>1" @click="decreaseConstraintsArraySize">− Remove</button>
                </div>
            </div>

            <!-- sample test cases -->
            <div class="glass card mb-3 rise rise-4">
                <div class="flex justify-between items-center mb-3 flex-wrap gap-2">
                    <h2 class="card__title">04 · Sample Test Cases <span class="text-dim" style="font-size:0.8rem;font-weight:400">(shown to candidates)</span></h2>
                    <div class="stepper">
                        <button type="button" @click="decSampleCount">−</button>
                        <strong>{{question.sampleTestCases.count}}</strong>
                        <button type="button" @click="incSampleCount">＋</button>
                    </div>
                </div>
                <div v-for="testCase in question.sampleTestCases.count" :key="'s'+testCase" class="section-block">
                    <div class="section-block__head">Sample {{testCase}}</div>
                    <div class="section-block__body">
                        <div class="field" style="margin-bottom:10px">
                            <label>Input</label>
                            <input type="text" class="input mono" placeholder="Separate arguments with whitespace, e.g. 1 2 3"
                                :class="{'is-invalid': (errors.sampleTestCases.input.required[testCase-1]===false || errors.sampleTestCases.input.valid[testCase-1]===false),
                                         'is-valid': (errors.sampleTestCases.input.required[testCase-1]===true && errors.sampleTestCases.input.valid[testCase-1]===true)}"
                                v-model="question.sampleTestCases.input[testCase-1]" @input="validateSampleTestCasesInput(testCase-1)">
                            <p class="err" v-if="errors.sampleTestCases.input.required[testCase-1]===false">Input is required</p>
                            <p class="err" v-else-if="errors.sampleTestCases.input.valid[testCase-1]===false">Input does not match the input format</p>
                            <p class="hint" v-else>Separate arguments with whitespace — e.g. <span class="mono">1 2 3</span></p>
                        </div>
                        <div class="field" style="margin-bottom:10px">
                            <label>Output</label>
                            <input type="text" class="input mono"
                                :class="{'is-invalid': (errors.sampleTestCases.output.required[testCase-1]===false || errors.sampleTestCases.output.valid[testCase-1]===false),
                                         'is-valid': (errors.sampleTestCases.output.required[testCase-1]===true && errors.sampleTestCases.output.valid[testCase-1]===true)}"
                                v-model="question.sampleTestCases.output[testCase-1]" @input="validateSampleTestCasesOutput(testCase-1)">
                            <p class="err" v-if="errors.sampleTestCases.output.required[testCase-1]===false">Output is required</p>
                            <p class="err" v-else-if="errors.sampleTestCases.output.valid[testCase-1]===false">Output does not match the output format</p>
                        </div>
                        <div class="field" style="margin-bottom:0">
                            <label>Explanation</label>
                            <textarea class="textarea" style="min-height:64px"
                                :class="{'is-invalid': errors.sampleTestCases.explanation[testCase-1]===false, 'is-valid': errors.sampleTestCases.explanation[testCase-1]===true}"
                                v-model="question.sampleTestCases.explanation[testCase-1]" @input="validateSampleTestCasesExplanation(testCase-1)"></textarea>
                            <p class="err" v-if="errors.sampleTestCases.explanation[testCase-1]===false">Explanation is required</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- hidden test cases -->
            <div class="glass card mb-3 rise rise-4">
                <div class="flex justify-between items-center mb-3 flex-wrap gap-2">
                    <h2 class="card__title">05 · Hidden Test Cases <span class="text-dim" style="font-size:0.8rem;font-weight:400">(used for grading)</span></h2>
                    <div class="stepper">
                        <button type="button" @click="decTestCount">−</button>
                        <strong>{{noOfTestCases}}</strong>
                        <button type="button" @click="incTestCount">＋</button>
                    </div>
                </div>
                <div v-for="testCase in noOfTestCases" :key="'t'+testCase" class="section-block">
                    <div class="section-block__head">Test Case {{testCase}}</div>
                    <div class="section-block__body">
                        <div class="field" style="margin-bottom:10px">
                            <label>Input</label>
                            <input type="text" class="input mono" placeholder="Separate arguments with a comma — e.g. 1, 2, 3"
                                :class="{'is-invalid': (errors.testCases.input.required[testCase-1]===false || errors.testCases.input.valid[testCase-1]===false),
                                         'is-valid': (errors.testCases.input.required[testCase-1]===true && errors.testCases.input.valid[testCase-1]===true)}"
                                v-model="question.testCases.input[testCase-1]" @input="validateTestCasesInput(testCase-1)">
                            <p class="err" v-if="errors.testCases.input.required[testCase-1]===false">Input is required</p>
                            <p class="err" v-else-if="errors.testCases.input.valid[testCase-1]===false">Input does not match the input format</p>
                            <p class="hint" v-else>Separate function arguments with a comma — e.g. <span class="mono">1, 2, 3</span></p>
                        </div>
                        <div class="field" style="margin-bottom:0">
                            <label>Expected output</label>
                            <input type="text" class="input mono"
                                :class="{'is-invalid': (errors.testCases.expectedOutput.required[testCase-1]===false || errors.testCases.expectedOutput.valid[testCase-1]===false),
                                         'is-valid': (errors.testCases.expectedOutput.required[testCase-1]===true && errors.testCases.expectedOutput.valid[testCase-1]===true)}"
                                v-model="expectedOutputArr[testCase-1]" @input="validateTestCasesExpectedOutput(testCase-1)">
                            <p class="err" v-if="errors.testCases.expectedOutput.required[testCase-1]===false">Output is required</p>
                            <p class="err" v-else-if="errors.testCases.expectedOutput.valid[testCase-1]===false">Output does not match the output format</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex gap-2 mt-4 rise rise-4">
                <button type="submit" class="btn btn--primary" :disabled="addStatus=='LOADING'">
                    <span class="spinner spinner--dark" v-if="addStatus=='LOADING'"></span>
                    <span v-else>Add Question</span>
                </button>
                <button type="reset" class="btn btn--ghost">Reset</button>
            </div>
        </form>
    </div>
</div>
</template>
<script>
import {addQuestion} from '../services/questions';
import ExaminerNavBar from './ExaminerNavBar.vue';
import Vue from 'vue';
export default {
    name: 'AddQuestionPage',
    data () {
        return {
            status: '',
            addStatus: '',
            inputFormatArraySize: 1,
            outputFormatArraySize: 1,
            constraintsArraySize: 1,
            question: {
                name: '',
                statement: '',
                inputFormat: {
                    type: [''],
                    description: ['']
                },
                outputFormat: {
                    type: '',
                    description: ''
                },
                constraints: [''],
                sampleTestCases: {
                    count: 1,
                    input: [''],
                    output: [''],
                    explanation: ['']
                },
                testCases: {
                    input: [''],
                    expectedOutput: '',
                },
                difficulty: '',
            },
            errors: {
                inputFormat: {
                    type: [],
                    description: []
                },
                outputFormat : {

                },
                constraints: [],
                sampleTestCases: {
                    input: {
                        required: [],
                        valid: []
                    },
                    output: {
                        required: [],
                        valid: []
                    },
                    explanation: []
                },
                testCases: {
                    input: {
                        required: [],
                        valid: []
                    },
                    expectedOutput: {
                        required: [],
                        valid: []
                    }
                }
            },
            noOfTestCases: 1,
            expectedOutputArr: [''],
            selected: null
        }
    },
    methods: {
        increaseInputArraySize(){
            this.inputFormatArraySize +=1;
        },
        decreaseInputArraySize(){
            this.inputFormatArraySize = this.inputFormatArraySize > 1 ? this.inputFormatArraySize-1 :this.inputFormatArraySize;
        },
        increaseConstraintsArraySize(){
            this.constraintsArraySize +=1;
        },
        decreaseConstraintsArraySize(){
            this.constraintsArraySize = this.constraintsArraySize > 1 ? this.constraintsArraySize-1 :this.constraintsArraySize;
        },
        incSampleCount() {
            this.question.sampleTestCases.count += 1;
        },
        decSampleCount() {
            if (this.question.sampleTestCases.count > 1) this.question.sampleTestCases.count -= 1;
        },
        incTestCount() {
            this.noOfTestCases += 1;
        },
        decTestCount() {
            if (this.noOfTestCases > 1) this.noOfTestCases -= 1;
        },
        async addQuestionForm(){
            let data = this.question;
            if(this.questionIsValid() )
                {
                    data.testCases.expectedOutput = `{`;
                    for(let i=0;i<this.expectedOutputArr.length-1;i++){
                        data.testCases.expectedOutput += `${this.expectedOutputArr[i]},`
                    }
                    data.testCases.expectedOutput += `${this.expectedOutputArr[this.expectedOutputArr.length-1]}}`;

                    for (let i=0;i<data.testCases.input.length;i++) {
                        data.testCases.input[i] = `{${data.testCases.input[i]}}`;
                    }
                try{
                    this.addStatus = 'LOADING';
                    const response = await addQuestion(data);
                    Vue.$toast.success('Question - '+response.data.name +' Added Successfully');
                    this.onReset();
                } catch(error){
                    Vue.$toast.error(error.message);

                }
                this.addStatus = 'LOADED';
                }
            else {
                Vue.$toast.error('Please Enter A Valid Question');
                this.status = 'INVALID';
            }
        },
        questionIsValid() {
            this.validateName();
            this.validateStatement();
            this.validateDifficulty();
            for (let i=0; i< (this.question.inputFormat.type.length ) ;i++) {
                this.validateInputTypeDescription(i);
                this.validateInputFormatDescription(i);
            }
            this.validateOutputTypeDescription();
            this.validateOutputFormatDescription();
            for (let i=0;i< this.question.constraints.length ; i++) {
                this.validateConstraints(i);
            }
            for (let i=0;i< this.question.sampleTestCases.input.length;i++) {
                this.validateSampleTestCasesInput(i);
                this.validateSampleTestCasesOutput(i);
                this.validateSampleTestCasesExplanation(i);
            }
            for (let i=0;i< this.question.testCases.input.length;i++) {
                this.validateTestCasesExpectedOutput(i);
                this.validateTestCasesInput(i);
            }

            return (this.errors.name &&
                    this.errors.statement &&
                        this.errors.inputFormat.type.reduce((acc, cur) => acc&&cur )&&
                            this.errors.inputFormat.description.reduce((acc, cur) => acc&&cur) &&
                this.errors.constraints.reduce((acc, cur) => acc&&cur) &&
                    this.errors.difficulty &&
                        this.errors.sampleTestCases.input.required.reduce((acc, cur) => acc&&cur) &&
                        this.errors.sampleTestCases.input.valid.reduce((acc, cur) => acc&&cur) &&
                            this.errors.sampleTestCases.output.required.reduce((acc, cur) => acc&&cur) &&
                            this.errors.sampleTestCases.output.valid.reduce((acc, cur) => acc&&cur) &&
                                this.errors.sampleTestCases.explanation.reduce((acc, cur) => acc&&cur) &&
                this.errors.testCases.input.required.reduce((acc, cur) => acc&&cur) &&
                this.errors.testCases.input.valid.reduce((acc, cur) => acc&&cur) &&
                    this.errors.testCases.expectedOutput.required.reduce((acc, cur) => acc&&cur) &&
                    this.errors.testCases.expectedOutput.valid.reduce((acc, cur) => acc&&cur))
        },
        validateName() {
            this.errors = {
                ...this.errors,
                name : (this.question.name == '') ? false : true
            }
        },
        validateStatement() {
            this.errors = {
                ...this.errors,
                statement : (this.question.statement == '') ? false : true
            }
        },
        validateInputFormatDescription(index) {
            this.$set(this.errors.inputFormat.description, index, this.question.inputFormat.description[index] == '' ? false : true);
        },
        validateInputTypeDescription(index) {
            this.$set(this.errors.inputFormat.type, index, (this.question.inputFormat.type[index] == '' || this.question.inputFormat.type[index] == null) ? false : true);
        },
        validateOutputFormatDescription() {
            this.$set(this.errors.outputFormat, 'description', this.question.outputFormat.description == '' ? false : true);
        },
        validateOutputTypeDescription() {
            this.$set(this.errors.outputFormat, 'type', (this.question.outputFormat.type == '' || this.question.outputFormat.type == null) ? false : true);
        },
        validateConstraints(index) {
            this.$set(this.errors.constraints, index, this.question.constraints[index] == '' ? false : true);
        },
        validateDifficulty() {
            this.errors = {
                ...this.errors,
                difficulty : (this.question.difficulty == '') ? false : true
            }
        },
        validateSampleTestCasesInput(index) {
            this.$set(this.errors.sampleTestCases.input.required, index, this.question.sampleTestCases.input[index] == '' || this.question.sampleTestCases.input[index] == null ? false : true);
            if (this.question.inputFormat.type.length == 0 || this.question.inputFormat.description.length == 0)
                {
                    this.$set(this.errors.sampleTestCases.input.valid, index, false);
                    return;
                }

            let args = (this.question.sampleTestCases.input[index] || '').split(" ");
            if (args.length != this.question.inputFormat.type.length || args[args.length-1] == '')
                {
                    this.$set(this.errors.sampleTestCases.input.valid, index, false);
                    return;
                }
             for (let i=0; i<args.length; i++) {
                if ( this.question.inputFormat.type[i] == 'int') {
                    if (!/^-?[0-9]+$/.test(args[i]))
                        {
                            this.$set(this.errors.sampleTestCases.input.valid, index, false);
                            return;
                        }
                }
                if ( this.question.inputFormat.type[i] == 'String') {
                    if (/[0-9]/.test(args[i]))
                        {
                            this.$set(this.errors.sampleTestCases.input.valid, index, false);
                            return;
                        }
                }
            }
            this.$set(this.errors.sampleTestCases.input.valid, index, true);
        },
        validateSampleTestCasesOutput(index) {
            this.$set(this.errors.sampleTestCases.output.required, index, this.question.sampleTestCases.output[index] == '' || this.question.sampleTestCases.output[index] == null ? false : true);

            if (this.question.outputFormat.type == '' || this.question.outputFormat.description == '')
                {
                    this.$set(this.errors.sampleTestCases.output.valid, index, false);
                    return;
                }
                if ( this.question.outputFormat.type == 'int') {
                    if (!/^-?[0-9]+$/.test(this.question.sampleTestCases.output[index]))
                        {
                            this.$set(this.errors.sampleTestCases.output.valid, index, false);
                            return;
                        }
                }
                if ( this.question.outputFormat.type == 'String') {
                    if (/[0-9]/.test(this.question.sampleTestCases.output[index]))
                        {
                            this.$set(this.errors.sampleTestCases.output.valid, index, false);
                            return;
                        }
                }
            this.$set(this.errors.sampleTestCases.output.valid, index, true);
        },
        validateSampleTestCasesExplanation(index) {
            this.$set(this.errors.sampleTestCases.explanation, index,
                this.question.sampleTestCases.explanation[index] == '' || this.question.sampleTestCases.explanation[index] == null ? false : true);
        },
        validateTestCasesInput(index) {
            this.$set(this.errors.testCases.input.required, index, this.question.testCases.input[index] == '' || this.question.testCases.input[index] == null ? false : true);
            if (this.question.inputFormat.type.length == 0 || this.question.inputFormat.description.length == 0)
                {
                    this.$set(this.errors.testCases.input.valid, index, false);
                    return;
                }

            let args = (this.question.testCases.input[index] || '').split(", ");
            if (args.length != this.question.inputFormat.type.length || args[args.length-1] == '')
                {
                    this.$set(this.errors.testCases.input.valid, index, false);
                    return;
                }
             for (let i=0; i<args.length; i++) {
                if ( this.question.inputFormat.type[i] == 'int') {
                    if (!/^-?[0-9]+$/.test(args[i]))
                        {
                            this.$set(this.errors.testCases.input.valid, index, false);
                            return;
                        }
                }
                if ( this.question.inputFormat.type[i] == 'String') {
                    if (/[0-9]/.test(args[i]))
                        {
                            this.$set(this.errors.testCases.input.valid, index, false);
                            return;
                        }
                }
            }
            this.$set(this.errors.testCases.input.valid, index, true);
        },
        validateTestCasesExpectedOutput(index) {
            this.$set(this.errors.testCases.expectedOutput.required, index, this.expectedOutputArr[index] == '' || this.expectedOutputArr[index] == null ? false : true);
            if (this.question.outputFormat.type == '' || this.question.outputFormat.description == '')
                {
                    this.$set(this.errors.testCases.expectedOutput.valid, index, false);
                    return;
                }
                if ( this.question.outputFormat.type == 'int') {
                    if (!/^-?[0-9]+$/.test(this.expectedOutputArr[index]))
                        {
                            this.$set(this.errors.testCases.expectedOutput.valid, index, false);
                            return;
                        }
                }
                if ( this.question.outputFormat.type == 'String') {
                    if (/[0-9]/.test(this.expectedOutputArr[index]))
                        {
                            this.$set(this.errors.testCases.expectedOutput.valid, index, false);
                            return;
                        }
                }
            this.$set(this.errors.testCases.expectedOutput.valid, index, true);
        },
        onReset() {
            this.question = {
                name: '',
                statement: '',
                inputFormat: {
                    type: [''],
                    description: ['']
                },
                outputFormat: {
                    type: '',
                    description: ''
                },
                constraints: [''],
                sampleTestCases: {
                    count: 1,
                    input: [''],
                    output: [''],
                    explanation: ['']
                },
                testCases: {
                    input: [''],
                    expectedOutput: '',
                },
                difficulty: '',
            }
            this.inputFormatArraySize = 1;
            this.constraintsArraySize = 1;
            this.noOfTestCases = 1;
            this.expectedOutputArr = [''];
            this.errors = {
                inputFormat: { type: [], description: [] },
                outputFormat : {},
                constraints: [],
                sampleTestCases: {
                    input: { required: [], valid: [] },
                    output: { required: [], valid: [] },
                    explanation: []
                },
                testCases: {
                    input: { required: [], valid: [] },
                    expectedOutput: { required: [], valid: [] }
                }
            };
        }
    },
    components: {
        ExaminerNavBar}
}
</script>
